"use client";

import Image from "next/image";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EJEDU_COLORS } from "@/lib/theme/ej-education";
import { SLIDER_ITEMS } from "@/fixtures/ej-education/fixtures";

/* ────────────────────────────────────────────────────────
   Top Slider — V2-styled re-imagining of the legacy slider.
   3 slides, 16:6 banner aspect, primary-blue circular nav
   buttons, centered with the body grid.
   ──────────────────────────────────────────────────────── */
export function TopSlider() {
  const [index, setIndex] = useState(0);
  const total = SLIDER_ITEMS.length;

  const next = () => setIndex((i) => (i + 1) % total);
  const prev = () => setIndex((i) => (i - 1 + total) % total);

  useEffect(() => {
    const id = setInterval(next, 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      className="mx-auto w-full max-w-[1280px] px-6 md:px-10"
      aria-label="信報教育精選輪播"
    >
      <div
        className="relative w-full overflow-hidden"
        style={{
          aspectRatio: 21 / 11,
          backgroundColor: EJEDU_COLORS.surfaceAlt,
        }}
      >
        {SLIDER_ITEMS.map((slide, i) => (
          <div
            key={slide.src}
            aria-hidden={i !== index}
            className="absolute inset-0 transition-opacity duration-500"
            style={{ opacity: i === index ? 1 : 0 }}
          >
            <Image
              src={slide.src}
              alt={slide.alt}
              fill
              priority={i === 0}
              sizes="100vw"
              className="object-contain"
              unoptimized
            />
          </div>
        ))}

        <button
          type="button"
          onClick={prev}
          aria-label="上一張"
          className="absolute left-4 top-1/2 inline-flex h-8 w-8 sm:h-12 sm:w-12 -translate-y-1/2 items-center justify-center transition-colors"
          style={{
            backgroundColor: EJEDU_COLORS.primary,
            color: EJEDU_COLORS.textOnDark,
            borderRadius: 999,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = EJEDU_COLORS.primaryDark;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = EJEDU_COLORS.primary;
          }}
        >
          <ChevronLeft className="h-6 w-6" />
        </button>

        <button
          type="button"
          onClick={next}
          aria-label="下一張"
          className="absolute right-4 top-1/2 inline-flex h-8 w-8 sm:h-12 sm:w-12 -translate-y-1/2 items-center justify-center transition-colors"
          style={{
            backgroundColor: EJEDU_COLORS.primary,
            color: EJEDU_COLORS.textOnDark,
            borderRadius: 999,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = EJEDU_COLORS.primaryDark;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = EJEDU_COLORS.primary;
          }}
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {SLIDER_ITEMS.map((slide, i) => (
            <button
              key={slide.src}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`第 ${i + 1} 張幻燈片`}
              aria-current={i === index}
              // The dots had `transition-all` beside an inline `backgroundColor`
              // and no hover class, so they were the last controls on the page
              // with no pointer response. The resting fill moves to a custom
              // property; the inactive dots brighten to solid white, the active
              // one darkens, so whichever you are pointing at answers.
              className="block bg-[color:var(--rest-bg)] transition-all duration-150 hover:bg-[color:var(--hover-bg)] motion-reduce:transition-none"
              style={
                {
                  width: i === index ? 28 : 8,
                  height: 8,
                  borderRadius: 999,
                  ["--rest-bg" as string]:
                    i === index ? EJEDU_COLORS.primary : "rgba(255,255,255,0.7)",
                  ["--hover-bg" as string]:
                    i === index ? EJEDU_COLORS.primaryDark : "#ffffff",
                } as React.CSSProperties
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
}
